import i18n from '@/utils/i18n';
import React from 'react';
import { Button, Props as ButtonProps } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ListOption } from '@/components/list-item';
import { OrderItemListFieldsFragment } from '@/types';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { SubstitutionAction } from '@/enums';

export interface OriginalVariantPack {
  available_quantity: number;
  display_name: string;
  requested_quantity: number;
  uuid: string;
}

export interface SubstitutionType {
  action?: SubstitutionAction;
  order_item_available_quantity?: number;
  original_variant_pack: OriginalVariantPack;
}

export interface ItemWithSubstitute<T> {
  item: T;
  substitution: SubstitutionType | null;
}

export interface SubstitutionGroupMetadata<T> {
  orderItems: ItemWithSubstitute<T>[];
  substitutionAction: SubstitutionAction | null;
  substitutionVariantPack: OriginalVariantPack | null;
}

export type OrderItemWithSubstitutionType<T> = ListOption<
  SubstitutionGroupMetadata<T>
>;

export function groupBySubstitution<T extends OrderItemListFieldsFragment>(
  orderItems: T[],
): T[][] {
  const subVPToIndexMap = orderItems.reduce<{
    [subVPUUID: string]: number[];
  }>((result, oi, idx) => {
    const substitution = oi.substitution;
    if (substitution) {
      const subsArray = result[substitution.original_variant_pack.uuid];
      if (subsArray) {
        if (oi.variants_pack.uuid === substitution.original_variant_pack.uuid) {
          subsArray.unshift(idx);
        } else {
          subsArray.push(idx);
        }
      } else {
        result[substitution.original_variant_pack.uuid] = [idx];
      }
    }
    return result;
  }, {});

  const pushedOrderItemUUIDs = new Set<string>();
  const groupedItems = orderItems.reduce<T[][]>((acc, oi) => {
    if (pushedOrderItemUUIDs.has(oi.uuid)) {
      return acc;
    }
    const { substitution } = oi;
    if (!substitution) {
      pushedOrderItemUUIDs.add(oi.uuid);
      acc.push([oi]);
    } else {
      const subVPIndices =
        subVPToIndexMap[substitution.original_variant_pack.uuid];
      const allItems = (subVPIndices || []).map((subVPIndex) => {
        pushedOrderItemUUIDs.add(orderItems[subVPIndex].uuid);
        return orderItems[subVPIndex];
      });
      acc.push(allItems);
    }
    return acc;
  }, []);

  return groupedItems;
}

export function shouldRenderSeparatorForSubItems<T>(
  leadingItem: OrderItemWithSubstitutionType<T>,
  trailingItem: OrderItemWithSubstitutionType<T>,
) {
  const firstItemSubVariantPack = leadingItem.metadata?.substitutionVariantPack;
  const secondItemSubVariantPack =
    trailingItem.metadata?.substitutionVariantPack;

  return firstItemSubVariantPack?.uuid === secondItemSubVariantPack?.uuid;
}

type SubstitutionActionOption = Omit<ButtonProps, 'children'> & {
  children: string;
};

interface Props {
  action: SubstitutionAction | null;
  actionButtons?: SubstitutionActionOption[];
  children: React.ReactNode;
  headerStyle?: StyleProp<ViewStyle>;
  noActionText: string;
  originalVariantPack: OriginalVariantPack;
  resetButtonProps?: ButtonProps;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
}

const RESET_BUTTON_HIT_SLOP = {
  bottom: 4,
  left: 0,
  right: 8,
  top: 4,
};

export const SubstitutionItemWrapper: React.FC<Props> = ({
  action,
  actionButtons,
  children,
  headerStyle,
  noActionText,
  originalVariantPack,
  resetButtonProps,
  style,
  textStyle,
}) => {
  const [styles] = useStyles();

  const noActionTaken = !action;
  const doNotReplace = action === SubstitutionAction.DO_NOT_REPLACE;

  return (
    <View style={[styles.base, style]}>
      <View
        style={[
          styles.header,
          noActionTaken ? styles.headerNoAction : styles.headerActionTaken,
          headerStyle,
        ]}
      >
        <View style={styles.actionTitleWrapper}>
          <Text
            numberOfLines={1}
            style={[
              TYPOGRAPHY.xsmall,
              FONTS.primarySemiBold,
              noActionTaken ? styles.headerTextNoAction : null,
              textStyle,
            ]}
          >
            {noActionTaken
              ? noActionText
              : doNotReplace
                ? i18n.t('do_not_substitute_title')
                : i18n.t('substitute_item_with', {
                    itemName: originalVariantPack.display_name,
                    quantity: originalVariantPack.requested_quantity,
                  })}
          </Text>
          {action && resetButtonProps ? (
            <Button
              hitSlop={RESET_BUTTON_HIT_SLOP}
              loadingProps={{
                style: styles.resetLoading,
              }}
              size="xsmall"
              style={styles.resetButton}
              textStyle={styles.resetText}
              type="tertiary"
              {...resetButtonProps}
            >
              {i18n.t('reset')}
            </Button>
          ) : null}
        </View>
      </View>
      {children}
      {noActionTaken && actionButtons?.length ? (
        <View style={styles.footerNoAction}>
          {actionButtons.map((buttonProps, idx) => (
            <Button
              containerStyle={styles.noActionButtonContainer}
              key={`sub-action-${buttonProps.children}`}
              style={[
                styles.noActionButton,
                idx !== actionButtons.length - 1
                  ? styles.noActionButtonLeft
                  : null,
              ]}
              textStyle={styles.actionButtonText}
              type="secondary"
              {...buttonProps}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actionButtonText: {
    textAlign: 'center',
  },
  actionTitleWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  base: {
    borderColor: theme.colors.blueAttention,
    borderRadius: 4,
    borderWidth: 1,
    flex: 1,
  },
  footerNoAction: {
    borderTopColor: theme.colors.blueAttention,
    borderTopWidth: 1,
    flex: 1,
    flexDirection: 'row',
  },
  header: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  headerActionTaken: {
    backgroundColor: theme.colors.blueAction,
  },
  headerNoAction: {
    backgroundColor: theme.colors.blueAttention,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  headerTextNoAction: {
    color: theme.colors.white,
  },
  noActionButton: {
    borderRadius: 0,
    borderWidth: 0,
  },
  noActionButtonContainer: {
    flex: 1,
  },
  noActionButtonLeft: {
    borderColor: theme.colors.blueAttention,
    borderRightWidth: 1,
  },
  resetButton: {
    paddingHorizontal: 0,
    paddingLeft: 16,
    paddingVertical: 0,
  },
  resetLoading: {
    alignSelf: 'flex-end',
  },
  resetText: {
    color: theme.colors.blueAttention,
    fontSize: theme.fontSizes.xsmall,
  },
}));
